import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { AsyncPipe, JsonPipe, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FlexModule } from '@angular/flex-layout/flex';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { createAsyncStore, loadingFor } from '@ngneat/loadoff';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  ApplicationSort, ApplicationTypes, ConfirmationDialogResult,
  DEFAULT_LIMIT, DEFAULT_OFFSET, displayApplicationDateTime,
  isAssociatedWithAggregator, isInternalUser, PortalLoginUser, PromotionResponse,
  SimplifiedApplication, STAGE_NAMES_ALL, STAGE_NAMES_APPROVED, STAGE_NAMES_CLOSED_WON, STAGE_NAMES_IN_SETTLEMENT,
  STAGE_NAMES_UNDER_REVIEW, StageNameType, User
} from '@portal-workspace/grow-shared-library';
import {
  ApplicationDialogService, ApplicationTypeIconComponent, CustomContentLoaderComponent, CustomPaginatorComponent,
  DataBoxComponent, getUser, MessageBoxComponent, MessageBoxComponentEvent, PortalHotToastService,
  SearchableSelectItem,
  SearchableSelectModule, setupUntilDestroy, TagBoxComponent, TopMenuService
} from '@portal-workspace/grow-ui-library';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AdminService } from '../../service/admin.service';
import { ApplicationService, } from '../../service/application.service';
import { HeartbeatService } from '../../service/heartbeat.service';
import { ApplicationStage, ApplicationType, CreditOfficer, formatStatistic, LocalApplicationsDbService, OpportunityOwner, SettlementOfficer } from '../../service/local-applications-db.service';
import {
  navigationUrlForAllApplication,
  navigationUrlForApplicationWithQueryParams,
  navigationUrlForAssetProductSelectorWithQueryParams,
  navigationUrlForBusinessFinanceWithQueryParams,
  navigationUrlForBusinessOverdraftWithQueryParams,
  navigationUrlForInsurancePremiumWithQueryParams
} from '../../service/navigation-urls';
import { ApplicationsGraphComponent, ApplicationsGraphData } from './applications-graph';

export class InternalDataSource extends DataSource<SimplifiedApplication> {

  subject: BehaviorSubject<SimplifiedApplication[]> = new BehaviorSubject<SimplifiedApplication[]>([]);

  connect(collectionViewer: CollectionViewer): Observable<SimplifiedApplication[]> {
    return this.subject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.subject.complete();
  }

  update(applications: SimplifiedApplication[]) {
    this.subject.next(applications);
  }
}

const MAX_DATE: number = moment().add(1, 'day').startOf('day').toDate().getTime();
const INCEPTION_DATE: number = moment('2015-01-01').startOf('day').toDate().getTime();

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
    templateUrl: './applications.page.html',
    styleUrls: ['./applications.page.scss'],
    standalone: true,
    imports: [
      MatFormFieldModule, MatSelectModule, FormsModule, ReactiveFormsModule, MessageBoxComponent, 
      MatIconModule, MatMenuModule, FlexModule, NgClass, CustomContentLoaderComponent, DataBoxComponent, 
      MatFormFieldModule, JsonPipe, MatInputModule, MatSelectModule, MatOptionModule, ApplicationTypeIconComponent, 
      MatTooltipModule, MatTableModule, MatSortModule, TagBoxComponent, CustomPaginatorComponent, AsyncPipe, 
      SearchableSelectModule, MatProgressBarModule, MatCardModule, ApplicationsGraphComponent, MatSliderModule, 
      MatButtonModule, MatCheckboxModule, MatListModule
    ]
})
export class ApplicationsPage implements OnInit, AfterViewInit, OnDestroy {

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent) {
    localStorage.setItem('needToRefresh', 'true');
  }

  STAGE_NAMES_UNDER_REVIEW = STAGE_NAMES_UNDER_REVIEW;
  STAGE_NAMES_IN_SETTLEMENT = STAGE_NAMES_IN_SETTLEMENT;
  STAGE_NAMES_CLOSED_WON = STAGE_NAMES_CLOSED_WON;
  STAGE_NAMES_APPROVED = STAGE_NAMES_APPROVED;

  MAX_DATE = MAX_DATE;

  subscriptions: Subscription[] = [];
  loader = loadingFor('tableLoading');
  displayColumns = ['appId', 'appName', 'status', 'stage', 'actions'];

  totalApplicationsUnderReview = '0';
  totalApplicationsInSettlement = '0';
  totalApplicationsClosedWon = '0';
  totalApplicationsApproved = '0';
  isProgressBarVisible: boolean = false;
  lastUpdatedMessage = '';
  limit: number = DEFAULT_LIMIT;
  offset: number = DEFAULT_OFFSET;
  total: number = 0;
  sorts?: ApplicationSort = undefined;

  isInternalUser = isInternalUser;

  loggedInUser: User | null = getUser();
  completedInitialLoad = false;

  user: PortalLoginUser | null = null;
  store = createAsyncStore();
  errorTitle = 'Error Occurred!';
  errorMessage = 'Please try again.';

  promo?: PromotionResponse;
  showFilters = false;
  toggleMyApplicationsBtn = false;
  showMyApplications = false;
  disabled = false;
  selectedFiltersCount = 0;
  
  opportunityOwners: SearchableSelectItem[] = [];
  creditOfficers: SearchableSelectItem[] = [];
  settlementOfficers: SearchableSelectItem[] = [];
  applicationStages: SearchableSelectItem[] = STAGE_NAMES_ALL.map((stage) => ({
    id: stage,
    label: stage,
    value: stage,
  }));
  applicationTypes: SearchableSelectItem[] = [
    { id: 'AssetFinance', label: 'Asset Finance', value: 'AssetFinance' },
    { id: 'BusinessLoans', label: 'Business Term Loan', value: 'BusinessLoans' }, 
    { id: 'BusinessOverdraft', label: 'Business Overdraft', value: 'BusinessOverdraft' }, 
    { id: 'InsurancePremium', label: 'Insurance Premium', value: 'InsurancePremium' },
    { id: 'Consumer', label: 'Consumer Asset Finance', value: 'Consumer' },
    { id: 'CorporateLoans', label: 'Corporate Loan', value: 'CorporateLoans' },
  ];
  
  selectedList: string = '';
  graphDataFormControl = new FormControl<ApplicationsGraphData[]>([]);
  
  sliderDisplayWith = (value: number) => {
    const twoHourMS = 2 * 60 * 60 * 1000;
    const date = new Date(Math.round(value/twoHourMS)*twoHourMS);
    return `${date.getDate() < 10 ? '0' : ''}${date.getDate()}/${date.getMonth() < 9 ? '0' : ''}${date.getMonth() + 1}/${date.getFullYear()-2000}`;
  }

  displayFields: null | 'slider' | 'dateRange' = null;
  dateRangeLastClicked: Date = new Date(0);
  displayTimeout: NodeJS.Timeout | undefined = undefined;
  minDate: number = moment().add(1, 'day').startOf('day').subtract(1, 'year').toDate().getTime();
  startDateFormControl = new FormControl<number>(this.minDate);
  endDateFormControl = new FormControl<number>(MAX_DATE);
  dateRangeFormGroup = new FormGroup({
    start: this.startDateFormControl,
    end: this.endDateFormControl
  });
  
  searchControl = new FormControl<string>('');
  applicationTypeControl = new FormControl<ApplicationTypes[]>([]);
  applicationStageControl = new FormControl<StageNameType[]>([]);
  filterOpportunityOwnerControl = new FormControl<string[]>([]);
  filterCreditOfficerControl = new FormControl<string[]>([]);
  filterSettlementOfficerControl = new FormControl<string[]>([]);
  statisticControl = new FormControl<string>('Count');
  
  filtersFormGroup = new FormGroup({
    searchControl: this.searchControl,
    applicationTypeControl: this.applicationTypeControl,
    applicationStageControl: this.applicationStageControl,
    filterOpportunityOwnerControl: this.filterOpportunityOwnerControl,
    filterCreditOfficerControl: this.filterCreditOfficerControl,
    filterSettlementOfficerControl: this.filterSettlementOfficerControl,
  });

  retry() {
    this.reload();
  }

  dataSource = new InternalDataSource();

  constructor(
    private applicationService: ApplicationService,
    private portalHotToastService: PortalHotToastService,
    private localApplicationDbService: LocalApplicationsDbService,
    private adminService: AdminService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private applicationDialogService: ApplicationDialogService,
    private topMenuService: TopMenuService,
    private heartbeatService: HeartbeatService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: ApplicationDialogService,
  ) {
  }

  private resetPagination() {
    this.offset = DEFAULT_OFFSET;
  }

  ngOnInit(): void {

    setupUntilDestroy(this);
    this.startMestageUpdate();

    this.reloadPromos();
    this.user = getUser();

    this.subscriptions.push(this.heartbeatService.subject.pipe(
      tap(r => {
        console.log('heartbeat event (applications page)', r);
        if (r.type === 'HeartbeatAccessLevelChangedEvent') {
          this.user = getUser();
          this.refresh();
          this.changeDetectorRef.detectChanges();
        }
      })
    ).subscribe());

    // this subscription is only called after retrieving data from db, i.e. refresh(true)
    this.subscriptions.push(this._applicationService().dataUpdates$.subscribe(r => {
      this.handleReloadData(r, true);
      this.isProgressBarVisible = false;
      this.completedInitialLoad = true;
      this.checkShowMyApplications();
    }));

    this.subscriptions.push(this.dateRangeFormGroup.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      tap(r => {
        this.resetPagination();
        this.countFilters(r);
      })
    ).subscribe());

    this.subscriptions.push(this.filtersFormGroup.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      tap((r: any) => {
        this.resetPagination();
        this.countFilters(r);
        if (this.loggedInUser?.Email) {
          const userInSettlement = (this.filterSettlementOfficerControl?.value ?? []).includes(this.loggedInUser.Email);
          const userInCredit = (this.filterCreditOfficerControl.value ?? []).includes(this.loggedInUser.Email);
          const userInOpportunity = (this.filterOpportunityOwnerControl.value ?? []).includes(this.loggedInUser.Email);
          this.updateQueryParams();
          if (!userInSettlement && !userInCredit && !userInOpportunity) {
            this.toggleMyApplicationsBtn = false;
          } else {
            this.toggleMyApplicationsBtn = true;
          }
        }
        if (this.completedInitialLoad) {
          this.refresh(false);
        }
      })
    ).subscribe());

    const queryParams = this.activatedRoute.snapshot.queryParams;
    if(!_.isEmpty(queryParams.search)  || !_.isEmpty(queryParams.filterOpportunityOwner)  || !_.isEmpty(queryParams.filterCreditOfficer)  || !_.isEmpty(queryParams.filterSettlementOfficer)  || !_.isEmpty(queryParams.applicationType)  || !_.isEmpty(queryParams.applicationStage) ){
        this.showFilters = true
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  ngAfterViewInit(): void {

    const queryParams = this.activatedRoute.snapshot.queryParams;
    const forceReload = queryParams?.reload;
    const limit = isNaN(_.toNumber(queryParams.limit)) ? DEFAULT_LIMIT : _.toNumber(queryParams.limit);
    const offset = isNaN(_.toNumber(queryParams.offset)) ? DEFAULT_OFFSET : _.toNumber(queryParams.offset);
    
    const search = queryParams.search ?? '';

    const filterOpportunityOwner: string[] = [].concat(
      queryParams.filterOpportunityOwner || []
    );
    const filterCreditOfficer: string[] = [].concat(
      queryParams.filterCreditOfficer || []
    );
    const filterSettlementOfficer: string[] = [].concat(
      queryParams.filterSettlementOfficer || []
    );
    const applicationType: ApplicationTypes[] = [].concat(
      queryParams.applicationType || []
    );
    const applicationStage: StageNameType[] = [].concat(
      queryParams.applicationStage || []
    );

    setTimeout(() => {
      this.offset = offset;
      this.limit = limit;
      this.sorts = queryParams.sorts ?? undefined;
      this.statisticControl.setValue(queryParams.statistic ?? 'Count');

      if (queryParams.minDate) this.minDate = Number(queryParams.minDate);
      if (queryParams.startDate) this.startDateFormControl.setValue(Number(queryParams.startDate));
      if (queryParams.endDate) this.endDateFormControl.setValue(Number(queryParams.endDate));

      this.filtersFormGroup.setValue({
        searchControl: search,
        applicationTypeControl: applicationType,
        applicationStageControl: applicationStage,
        filterOpportunityOwnerControl: filterOpportunityOwner,
        filterCreditOfficerControl: filterCreditOfficer,
        filterSettlementOfficerControl: filterSettlementOfficer,
      });
       
      const userNeedsRefresh = !['admin', 'analyst', 'operations', 'operations24', 'credit', 'salesam', 'salesbdm'].includes(this.user?.AccessLevel ?? '');
      if (forceReload === true || forceReload === "true" || userNeedsRefresh || !!localStorage.getItem('needToRefresh')) {
        localStorage.removeItem('needToRefresh');
        this.refresh(true);
      } else {
        this.refresh(false);
      }
    });
  }

  refresh(forceReload: boolean = true) {
    if(forceReload){
      if (this.isProgressBarVisible) return; // prevent multiple calls to refresh
      this.resetPagination();
    }

    let minDate: Date | undefined;
    let startDate: Date | undefined;
    if (!this.minDate || this.minDate <= INCEPTION_DATE) {
      minDate = undefined;
      startDate = undefined;
    } else {
      minDate = new Date(this.minDate);
      startDate = new Date(this.startDateFormControl.value ?? INCEPTION_DATE);
    }

    this.reload(
      forceReload,
      this.offset,
      this.limit,
      this.sorts,
      this.searchControl.value ?? undefined,
      this.applicationTypeControl.value ?? undefined, 
      this.applicationStageControl.value ?? undefined, 
      this.filterOpportunityOwnerControl.value ?? undefined, 
      this.filterCreditOfficerControl.value ?? undefined, 
      this.filterSettlementOfficerControl.value ?? undefined,
      startDate,
      this.endDateFormControl.value ? new Date(this.endDateFormControl.value) : undefined,
      minDate,
      this.statisticControl.value ?? 'Count'
    );
  }

  private reloadPromos() {
    this.subscriptions.push(
      this.adminService
        .getPromotionByType('application')
        .pipe(
          tap((r) => {
            this.promo = r.payload;
          })
        )
        .subscribe()
    );
  }

  countFilters(r: any) {
    this.selectedFiltersCount = Object.values(r).filter(v => (Array.isArray(v) ? v?.length ?? 0 : 0) > 0).length;
    if (this.searchControl.value) this.selectedFiltersCount++;
    if (this.startDateFormControl.value !== this.minDate || this.endDateFormControl.value !== MAX_DATE) {
      this.selectedFiltersCount++;
    }
  }

  getApplicantName(app: SimplifiedApplication) {
    const entityLegalName = (app as any)['CompanyName'];
    if (entityLegalName) {
      return entityLegalName;
    } else if (app.ApplicationType === 'Consumer') {
      const givenName = (app as any)['IndividualGivenName'];
      const surName = (app as any)['IndividualSurName'];
      if (givenName || surName) {
        return `${givenName ?? ''} ${surName ?? ''}`.trim();
      }
    }
    return '';
  }

  getApplicationStatus(app: SimplifiedApplication) {
    const stage = (app as any).StageName;
    const salesforceId = (app as any).SalesforceId;
    return this.applicationService.getApplicationStatus2(stage, salesforceId);
  }

  getApplicationStage(app: SimplifiedApplication) {
    const stage = (app as any).StageName;
    return this.applicationService.getApplicationStage2(stage);
  }

  getApplicationCreateTime(app: SimplifiedApplication) {
    return displayApplicationDateTime(moment(app.CreateTime));
  }

  reload(
    forceReload: boolean = false,
    offset: number = DEFAULT_OFFSET,
    limit: number = DEFAULT_LIMIT,
    sorts?: ApplicationSort,
    search?: string,
    applicationType?: ApplicationTypes[],
    applicationStage?: StageNameType[],
    filterOpportunityOwner?: string[],
    filterCreditOfficer?: string[],
    filterSettlementOfficer?: string[],
    startDate?: Date,
    endDate?: Date,
    minDate?: Date,
    statistic?: string
  ) {
    if (forceReload) this.isProgressBarVisible = true;
    this.store = createAsyncStore();
    this.subscriptions.push(this._applicationService().reload({
      forceReload,
      page: {offset, limit},
      filter: search,
      applicationStage,
      applicationType,
      sorts,
      filterOpportunityOwner,
      filterCreditOfficer,
      filterSettlementOfficer,
      dateFilter: { startDate, endDate: endDate ?? new Date(MAX_DATE) },
      minDate,
      statistic
    }).pipe(
      this.loader.tableLoading.track(),
      this.store.track(),
      this.portalHotToastService.topMenuLoadingObservable(),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: this.errorTitle, errorMessage: this.errorMessage, retryFn: this.retry.bind(this),
      }),
      tap((r: any) => {
        this.handleReloadData(r, !forceReload);
        if (!forceReload && !this.completedInitialLoad) {
          this.completedInitialLoad = true;
          this.checkShowMyApplications();
        }
      }),
    ).subscribe());
  }

  handleReloadData(r: any, updateMinDate: boolean) {

    this.totalApplicationsUnderReview = formatStatistic(r.totals.underReview, this.statisticControl.value ?? 'Count');
    this.totalApplicationsInSettlement = formatStatistic(r.totals.inSettlement, this.statisticControl.value ?? 'Count');
    this.totalApplicationsClosedWon = formatStatistic(r.totals.closedWon, this.statisticControl.value ?? 'Count');
    this.totalApplicationsApproved = formatStatistic(r.totals.approved, this.statisticControl.value ?? 'Count');

    this.opportunityOwners = this.mapOfficerToSelectItems(r.opportunityOwners, this.filterOpportunityOwnerControl.value ?? []);
    this.creditOfficers = this.mapOfficerToSelectItems(r.creditOfficers, this.filterCreditOfficerControl.value ?? []);
    this.settlementOfficers = this.mapOfficerToSelectItems(r.settlementOfficers, this.filterSettlementOfficerControl.value ?? []);
    this.applicationStages = this.mapApplicationFieldToSearchableSelectItems(r.applicationStages, this.applicationStageControl.value ?? []);
    this.applicationTypes = this.mapApplicationFieldToSearchableSelectItems(r.applicationTypes, this.applicationTypeControl.value ?? []);

    this.isMyApplicationsDisabled();
    this.limit = r.limit;
    this.offset = r.offset;
    this.total = r.total;

    this.dataSource.update(r.applications);
    this.graphDataFormControl.setValue(r.graphData);
    this.getLastUpdatedMessage();

    if (this.minDate <= INCEPTION_DATE && updateMinDate) {
      const earliestDate = r.graphData[0].segmentStart.toDate().getTime();
      this.minDate = moment(earliestDate).startOf('day').toDate().getTime();
      this.startDateFormControl.setValue(this.minDate);
    }
  }

  onPagination($event: PageEvent) {
    this.limit = $event.pageSize;
    this.offset = $event.pageIndex; // Only update offset from pagination event
  
    this.updateQueryParams();
    this.refresh(false);
  }

  updateQueryParams() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { 
        offset: this.offset,
        limit: this.limit,
        filter: this.searchControl.value ?? undefined,  // Ensure filters are included
        applicationType: this.applicationTypeControl.value ?? undefined,
        applicationStage: this.applicationStageControl.value ?? undefined,
        filterOpportunityOwner: this.filterOpportunityOwnerControl.value ?? undefined,
        filterCreditOfficer: this.filterCreditOfficerControl.value ?? undefined,
        filterSettlementOfficer: this.filterSettlementOfficerControl.value ?? undefined,
        startDate: this.startDateFormControl.value ?? undefined,
        endDate: this.endDateFormControl.value ?? undefined,
        minDate: this.minDate ?? undefined,
        statistic: this.statisticControl.value ?? undefined
      },
      queryParamsHandling: 'merge'
    });
  }
  
  toClass(applicationStatus: string | null) {
    return applicationStatus ? applicationStatus.replace(/\s/g, '') : '';
  }

  getLastUpdatedMessage() {
    const lastUpdatedTime = localStorage.getItem('lastUpdatedTime');
    if (lastUpdatedTime) {
      const lastUpdatedDate = new Date(lastUpdatedTime);
      const currentTime = new Date();

      const diffInMs = currentTime.getTime() - lastUpdatedDate.getTime(); // Difference in milliseconds
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60)); // Convert to minutes

      if (diffInMinutes === 0) {
        this.lastUpdatedMessage = 'Last updated just now';
      } else if (diffInMinutes === 1) {
        this.lastUpdatedMessage = 'Last updated 1 minute ago';
      } else if (diffInMinutes < 60) {
        this.lastUpdatedMessage = `Last updated ${diffInMinutes} minutes ago`;
      } else if (diffInMinutes >= 60 && diffInMinutes < 120) {
        this.lastUpdatedMessage = 'Last updated 1 hour ago';
      } else if (diffInMinutes >= 120 && diffInMinutes < 1440) {
        this.lastUpdatedMessage = `Last updated ${Math.floor(
          diffInMinutes / 60
        )} hours ago`;
      } else {
        this.lastUpdatedMessage = `Last updated ${Math.floor(
          diffInMinutes / 1440
        )} days ago`;
      }
    } else {
      this.lastUpdatedMessage = 'List has not been updated yet';
    }
  }

  startMestageUpdate() {
    this.getLastUpdatedMessage();
    setInterval(() => {
      this.getLastUpdatedMessage();
    }, 60000);
  }

  onSort($event: Sort) {
    if ($event.direction) {
      const dir = $event.direction === 'asc' ? 'ASC' : 'DESC';
      switch ($event.active) {
        case 'appId':
          this.sorts = [{ prop: 'BrokerAppId', dir }];
          break;
        case 'appName':
          this.sorts = [{ prop: 'CompanyName', dir }];
          break;
        case 'status':
          this.sorts = [{ prop: 'Status', dir }];
          break;
        case 'stage':
          this.sorts = [{ prop: 'AppInfoStageName', dir }];
          break;
      }
    } else {
      this.sorts = undefined;
    }
    this.refresh(false);
  }

  clearFilters() {
    this.onDateRangeClick('year');
    this.filtersFormGroup.setValue({
      searchControl: '',
      applicationTypeControl: [],
      applicationStageControl: [],
      filterOpportunityOwnerControl: [],
      filterCreditOfficerControl: [],
      filterSettlementOfficerControl: [],
    });
    this.updateQueryParams();
    this.refresh(false);
  }

  async onApplicationClick(
    $event: MouseEvent,
    application: SimplifiedApplication,
    newTab: boolean = false
  ) {
    const status = this.getApplicationStatus(application);
    const applicationId = application.ApplicationId;
    const queryParams = {
      reload: false,
      limit: this.limit,
      offset: this.offset,
      search: this.searchControl.value ?? undefined,
      applicationType: this.applicationTypeControl.value ?? undefined,
      applicationStage: this.applicationStageControl.value ?? undefined,
      filterOpportunityOwner:
        this.filterOpportunityOwnerControl.value ?? undefined,
      filterCreditOfficer: this.filterCreditOfficerControl.value ?? undefined,
      filterSettlementOfficer:
        this.filterSettlementOfficerControl.value ?? undefined,
      sorts: this.sorts ?? undefined,
      statistic: this.statisticControl.value ?? undefined
    };
    let urlTree: UrlTree;
    if (status === 'Draft') {
      switch (application.ApplicationType) {
        case 'AssetFinance':
          urlTree = navigationUrlForAssetProductSelectorWithQueryParams( this.router, applicationId, queryParams );
          break;
        case 'BusinessLoans':
          urlTree = navigationUrlForBusinessFinanceWithQueryParams( this.router, applicationId, queryParams );
          break;
        case 'BusinessOverdraft':
          urlTree = navigationUrlForBusinessOverdraftWithQueryParams( this.router, applicationId, queryParams );
          break;
        case 'InsurancePremium':
          urlTree = navigationUrlForInsurancePremiumWithQueryParams( this.router, applicationId, queryParams );
          break;
        default:
          // Removed types (Consumer, CorporateLoans, Commercial, TradeFinance, Solar)
          // are no longer editable -- send to the read-only review page.
          urlTree = navigationUrlForApplicationWithQueryParams( this.router, String(application.ApplicationId), 'app', queryParams );
          break;
      }
    } else {
      urlTree = navigationUrlForApplicationWithQueryParams( this.router, String(application.ApplicationId), 'app', queryParams );
    }
    if (newTab) {
      window.open(this.router.serializeUrl(urlTree), '_blank');
    } else {
      this.router.navigateByUrl(urlTree);
    }
  }

  onApplicationStageFilter(applicationStages: StageNameType[]) {
    let newAppStageList: StageNameType[] = [
      ...(this.applicationStageControl.value ?? []),
    ];
    for (const applicationStage of applicationStages) {
      if (newAppStageList.includes(applicationStage)) {
        newAppStageList = newAppStageList.filter(
          (stage) => stage !== applicationStage
        );
      } else {
        newAppStageList.push(applicationStage);
      }
    }
    this.applicationStageControl.setValue(newAppStageList);
    this.refresh(false);
  }

  showBrokerName(): boolean {
    return isInternalUser(this.user) || isAssociatedWithAggregator(this.user);
  }

  closePromo($event: MessageBoxComponentEvent) {
    $event.close();
  }

  _applicationService(): LocalApplicationsDbService {
    return this.localApplicationDbService;
  }

  onDeleteByAppId(applicationId: number) {
    this.subscriptions.push(this.applicationService.deleteApplicationByAppId(applicationId).pipe(
      this.portalHotToastService.snackBarObservable('Application deleted'),
      tap((r: any) => {
        if (r.status) { // deleted successfully
          this._applicationService().removeApplicationLocally(applicationId);
          this.refresh(true);
        }
      }),
    ).subscribe());
  }

  onDelete(applicationId: number) {
    this.subscriptions.push(
      this.applicationDialogService
        .openConfirmationDialog({
          message: 'Please confirm',
          subMessage: 'Do you want to delete this application?',
        })
        .afterClosed()
        .subscribe((r: ConfirmationDialogResult | undefined) => {
          if (r && r.readyForSubmission) {
            if (applicationId) {
              this.onDeleteByAppId(applicationId);
            }
          }
        })
    );
  }

  async onClickAllApplication($event: Event) {
    await this.router.navigate(navigationUrlForAllApplication());
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  selectionChange(field: string) {
    this.selectedList = field;
  }

  toggleMyApplications() {
    this.toggleMyApplicationsBtn = !this.toggleMyApplicationsBtn;
    if(this.toggleMyApplicationsBtn) {
      if (this.loggedInUser?.Email) {
        const newSettlementOfficers: string[] = [];
        const newCreditOfficers: string[] = [];
        const newOpportunityOwners: string[] = [];

        if (this.settlementOfficers.find(o => o.value === this.loggedInUser?.Email)) {
          newSettlementOfficers.push(this.loggedInUser?.Email);
        } else if (this.creditOfficers.find(o => o.value === this.loggedInUser?.Email)) {
          newCreditOfficers.push(this.loggedInUser?.Email);
        } else if (this.opportunityOwners.find(o => o.value === this.loggedInUser?.Email)) {
          newOpportunityOwners.push(this.loggedInUser?.Email);
        } else {
          this.dataSource.update([]);
        }

        this.filtersFormGroup.setValue({
          filterSettlementOfficerControl: newSettlementOfficers,
          filterCreditOfficerControl: newCreditOfficers,
          filterOpportunityOwnerControl: newOpportunityOwners,
          searchControl: this.searchControl.value,
          applicationTypeControl: this.applicationTypeControl.value,
          applicationStageControl: this.applicationStageControl.value
        });
      }
    } else {
      this.filtersFormGroup.setValue({
        filterSettlementOfficerControl: this.settlementOfficers.filter(o => o.value !== this.loggedInUser?.Email && this.filterSettlementOfficerControl.value?.includes(o.value)).map(o => o.value),
        filterCreditOfficerControl: this.creditOfficers.filter(o => o.value !== this.loggedInUser?.Email && this.filterCreditOfficerControl.value?.includes(o.value)).map(o => o.value),
        filterOpportunityOwnerControl: this.opportunityOwners.filter(o => o.value !== this.loggedInUser?.Email && this.filterOpportunityOwnerControl.value?.includes(o.value)).map(o => o.value),
        searchControl: this.searchControl.value,
        applicationTypeControl: this.applicationTypeControl.value,
        applicationStageControl: this.applicationStageControl.value
      });
    }
  }

  mapOfficerToSelectItems(
    items: OpportunityOwner[] | CreditOfficer[] | SettlementOfficer[],
    selectedList: string[]
  ) {
    const me = this.loggedInUser?.Email;

    const selectedArray = Array.isArray(selectedList)
      ? selectedList
      : [selectedList];

    items = items.sort((a, b) => {
      // Sort by count in descending order
      const aSelected = selectedArray.includes(a.email ?? '');
      const bSelected = selectedArray.includes(b.email ?? '');
      const aHasNone =
        a.firstName?.toLowerCase() === 'none' ||
        a.email?.toLowerCase() === 'none';
      const bHasNone =
        b.firstName?.toLowerCase() === 'none' ||
        b.email?.toLowerCase() === 'none';

      // First priority: selected items
      if (aSelected !== bSelected) {
        if (aSelected) return -1;
        return 1;
      }

      // Second priority: items with "none"
      if (aHasNone && !bHasNone) return -1;
      if (!aHasNone && bHasNone) return 1;

      // Third priority: count
      return (b.count ?? 0) - (a.count ?? 0);
    });

    const newItems = items.map((item) => ({
      id: item.email ?? '',
      value: item.email ?? '',
      label: `${item.firstName} ${item.lastName}`,
      searchTerm: `${item.firstName} ${item.lastName} (${item.email})`,
      priority: item.email === me,
      count: formatStatistic(
        item.count ?? 0,
        this.statisticControl.value ?? 'Count'
      ),
    }));

    // Ensure selected values are included but not duplicated
    selectedArray.forEach((email) => {
      if (!newItems.some((item) => item.id === email)) {
        const item =
          this.settlementOfficers.find((item) => item.value === email) ??
          this.creditOfficers.find((item) => item.value === email) ??
          this.opportunityOwners.find((item) => item.value === email);       
        newItems.push({
          id: item?.id ?? email,
          value: item?.value ?? email,
          label: item?.label ?? email,
          searchTerm: item?.searchTerm ?? email,
          priority: item?.priority ?? false,
          count: '0',
        });
      }
    });
    return newItems;
  }

  mapApplicationFieldToSearchableSelectItems(
    items: ApplicationStage[] | ApplicationType[], 
    selectedList: string[]
  ) {
    const newItems = items.map(item => {
      const label = () => {
        switch (item.name) {
          case 'AssetFinance':
            return 'Asset Finance';
          case 'BusinessLoans':
            return 'Business Term Loan';
          case 'BusinessOverdraft':
            return 'Business Overdraft';
          case 'Commercial':
            return 'Commercial Finance';
          case 'Consumer':
            return 'Consumer Asset Finance';
          case 'InsurancePremium':
            return 'Insurance Premium';
          case 'CorporateLoans':
            return 'Corporate Loan';
          case 'InvoiceFinance':
            return 'Invoice Finance';
          default:
            return item.name;
        }
      };
      return {
        id: item.name ?? '',
        value: item.name ?? '',
        label: label() ?? '',
        searchTerm: label() ?? '',
        count: formatStatistic(item.count ?? 0, this.statisticControl.value ?? 'Count')
      }
    });

    for (const field of selectedList) {
      if (!newItems.find(item => item.id === field)) {
        const item = this.applicationStages.find(item => item.name === field) ?? 
                     this.applicationTypes.find(item => item.name === field);
        newItems.push({
          id: item?.id ?? field,
          value: item?.value ?? field,
          label: item?.label ?? field,
          searchTerm: item?.searchTerm ?? field,
          count: '0'
        });
      }
    }

    return newItems;
  }

  onDateSelect = (event: any) => {
    this.refresh(false);
  }

  checkCanUseDateRange() {
    return (new Date().getTime() - this.dateRangeLastClicked.getTime()) <= 1000;
  }

  changeDisplayFields(newVal: 'slider' | 'dateRange' | null) {
    if (newVal == 'dateRange' || this.displayFields == 'dateRange') this.dateRangeLastClicked = new Date();
    clearTimeout(this.displayTimeout);
    this.displayTimeout = setTimeout(() => {
      this.displayFields = newVal;
    }, newVal ? 0 : 1000);
  }

  async onDateRangeClick(dateRange: string) {
    const oldMinDate = this.minDate;
    let newMinDate: Moment;
    switch (dateRange) {
      case '24hr':
        newMinDate = moment(MAX_DATE).subtract(1, 'day');
        break;
      case '48hr':
        newMinDate = moment(MAX_DATE).subtract(2, 'day');
        break;
      case 'week':
        newMinDate = moment(MAX_DATE).subtract(7, 'day');
        break;
      case 'month':
        newMinDate = moment(MAX_DATE).subtract(1, 'month');
        break;
      case 'year':
        newMinDate = moment(MAX_DATE).subtract(12, 'month');
        break;
      case 'all':
        newMinDate = moment(INCEPTION_DATE);
        const result = await this.dialogService.openConfirmationDialog({
          message: 'Load All Applications?',
          subMessage: 'This will take a while to load all applications. Do you want to proceed?'
        }).afterClosed().toPromise();
        if (!result || !result.readyForSubmission) {
          return;
        }
        break;
      default:
        return;
    }
    this.resetPagination();
    this.minDate = newMinDate.toDate().getTime();
    await new Promise(resolve => setTimeout(resolve, 10));
    this.startDateFormControl.setValue(newMinDate.toDate().getTime());
    this.endDateFormControl.setValue(MAX_DATE);

    if (newMinDate.toDate().getTime() < oldMinDate) {
      this.refresh(true);
    } else {
      this.refresh(false);
    }
  }

  onColumnClick(column: ApplicationsGraphData) {
    this.startDateFormControl.setValue(column.segmentStart.toDate().getTime());
    this.endDateFormControl.setValue(column.segmentEnd.toDate().getTime());
    this.refresh(false);
  }
  
  isMyApplicationsDisabled(): void {
    const isSettlementOfficer = this.settlementOfficers.find(
      (officer) => officer.value === this.loggedInUser?.Email
    );
    const isCreditOfficer = this.creditOfficers.find(
      (officer) => officer.value === this.loggedInUser?.Email
    );
    const isOpportunityOwner = this.opportunityOwners.find(
      (officer) => officer.value === this.loggedInUser?.Email
    );
    
    this.disabled = !isSettlementOfficer && !isCreditOfficer && !isOpportunityOwner;
  }

  checkShowMyApplications() {
    const {settlementOfficers, creditOfficers, opportunityOwners} = this.localApplicationDbService.getUnfilteredContacts();

    const isSettlementOfficer = settlementOfficers.find(
      (officer: any) => officer.email === this.loggedInUser?.Email
    );
    const isCreditOfficer = creditOfficers.find(
      (officer: any) => officer.email === this.loggedInUser?.Email
    );
    const isOpportunityOwner = opportunityOwners.find(
      (officer: any) => officer.email === this.loggedInUser?.Email
    );
    
    this.showMyApplications = !isSettlementOfficer && !isCreditOfficer && !isOpportunityOwner;
  }

  toggleStatistic() {
    const statistics = ['Value', 'Count'];
    this.statisticControl.setValue(statistics[(statistics.findIndex(s => s === this.statisticControl.value) + 1) % statistics.length]);
    this.updateQueryParams();
    this.refresh(false);
  }
}
